'use client';

import * as React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
    BoxIcon,
    CircuitBoardIcon,
    FileBarChartIcon,
    LayoutIcon,
    PlayIcon
} from 'lucide-react';
import { motion } from 'motion/react';

import { routes } from '@workspace/routes';
import { Badge } from '@workspace/ui/components/badge';
import { buttonVariants } from '@workspace/ui/components/button';
import { ScrollArea, ScrollBar } from '@workspace/ui/components/scroll-area';
import { Separator } from '@workspace/ui/components/separator';
import {
    UnderlinedTabs,
    UnderlinedTabsContent,
    UnderlinedTabsList,
    UnderlinedTabsTrigger
} from '@workspace/ui/components/tabs';
import { cn } from '@workspace/ui/lib/utils';

import { GridSection } from '~/components/fragments/grid-section';

function HeroPill(): React.JSX.Element {
    return (
        <motion.div
            initial={{ filter: 'blur(10px)', opacity: 0, y: -20 }}
            animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex items-center justify-center"
        >
            <Badge
                variant="outline"
                className="group h-8 cursor-default rounded-full px-3 text-xs font-medium shadow-xs duration-200 hover:bg-accent/50 sm:text-sm"
            >
                <div className="w-fit py-0.5 text-center text-xs text-primary sm:text-sm">
                    Neu!
                </div>
                <Separator
                    orientation="vertical"
                    className="mx-2"
                />
                KI-Buchhaltung jetzt live 🚀
            </Badge>
        </motion.div>
    );
}

function HeroTitle(): React.JSX.Element {
    return (
        <motion.div
            initial={{ filter: 'blur(10px)', opacity: 0, y: 20 }}
            animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
        >
            <h1 className="mt-6 text-center text-[32px] font-bold leading-[38px] tracking-[-0.8px] [font-kerning:none] xs:text-[40px] xs:leading-[46px] sm:text-[48px] sm:leading-[54px] sm:tracking-[-1.2px] md:text-[64px] md:leading-[68px] lg:text-[76px] lg:leading-[74px] lg:tracking-[-2px]">
                Vorbereitende Buchhaltung
                <br /> auf Autopilot
            </h1>
            <div className="mt-4 flex justify-center px-4">
                <Badge variant="secondary" className="text-xs font-normal text-muted-foreground bg-muted/50 border-border/50 backdrop-blur-sm sm:text-sm">
                    Inkl. Doppelte Buchführung & Multi-Währung
                </Badge>
            </div>
        </motion.div>
    );
}

function HeroDescription(): React.JSX.Element {
    return (
        <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
            className="mx-auto mt-3 max-w-[560px] text-balance text-center text-lg leading-[26px] text-muted-foreground sm:text-xl lg:mt-6"
        >
            Für Kapitalgesellschaften, die Zeit sparen wollen. Erfassen Sie Belege automatisch und übergeben Sie geprüfte Daten an Ihren Steuerberater.
        </motion.p>
    );
}

function HeroButtons(): React.JSX.Element {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.4 }}
            className="mx-auto flex w-full flex-col gap-2 px-7 sm:w-auto sm:flex-row sm:px-0"
        >
            <Link
                href={routes.marketing.Pricing}
                className={cn(
                    buttonVariants({
                        variant: 'default'
                    }),
                    'h-10 rounded-xl sm:h-9'
                )}
            >
                Lifetime Deal sichern
            </Link>
            <Link
                href={routes.marketing.Contact}
                className={cn(
                    buttonVariants({
                        variant: 'outline'
                    }),
                    'h-10 rounded-xl sm:h-9'
                )}
            >
                Kontakt aufnehmen
            </Link>
        </motion.div>
    );
}

function MainDashedGridLines(): React.JSX.Element {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.4 }}
        >
            <svg className="absolute left-[16.85%] top-0 hidden h-full w-px mask-[linear-gradient(to_bottom,#0000,#000_128px,#000_calc(100%-24px),#0000)] lg:block">
                <line
                    x1="0.5"
                    y1="0"
                    x2="0.5"
                    y2="100%"
                    strokeLinecap="round"
                    strokeDasharray="5 5"
                    stroke="var(--border)"
                />
            </svg>
            <svg className="absolute right-[16.85%] top-0 hidden h-full w-px mask-[linear-gradient(to_bottom,#0000,#000_128px,#000_calc(100%-24px),#0000)] lg:block">
                <line
                    x1="0.5"
                    y1="0"
                    x2="0.5"
                    y2="100%"
                    strokeLinecap="round"
                    strokeDasharray="5 5"
                    stroke="var(--border)"
                />
            </svg>
            <svg className="absolute bottom-[52px] left-[calc(50%-50vw)] hidden h-px w-screen mask-[linear-gradient(to_right,#0000,#000_100px,#000_calc(100%-100px),#0000)] lg:block">
                <line
                    x1="0"
                    y1="0.5"
                    x2="100%"
                    y2="0.5"
                    strokeLinecap="round"
                    strokeDasharray="5 5"
                    stroke="var(--border)"
                />
            </svg>
        </motion.div>
    );
}

function SupportiveDashedGridLines(): React.JSX.Element {
    return (
        <>
            <svg className="absolute left-[calc(50%-50vw)] top-[-25px] z-10 hidden h-px w-screen mask-[linear-gradient(to_right,#0000,#000_100px,#000_calc(100%-100px),#0000)] lg:block">
                <line
                    x1="0"
                    y1="0.5"
                    x2="100%"
                    y2="0.5"
                    strokeLinecap="round"
                    strokeDasharray="5 5"
                    stroke="var(--border)"
                />
            </svg>
            <svg className="absolute left-[calc(50%-50vw)] top-0 z-10 hidden h-px w-screen mask-[linear-gradient(to_right,#0000,#000_100px,#000_calc(100%-100px),#0000)] lg:block">
                <line
                    x1="0"
                    y1="0.5"
                    x2="100%"
                    y2="0.5"
                    strokeLinecap="round"
                    strokeDasharray="5 5"
                    stroke="var(--border)"
                />
            </svg>
            <svg className="absolute left-[calc(50%-50vw)] top-[52px] z-10 hidden h-px w-screen mask-[linear-gradient(to_right,#0000,#000_100px,#000_calc(100%-100px),#0000)] lg:block">
                <line
                    x1="0"
                    y1="0.5"
                    x2="100%"
                    y2="0.5"
                    strokeLinecap="round"
                    strokeDasharray="5 5"
                    stroke="var(--border)"
                />
            </svg>
        </>
    );
}

import { ProcessingDemo } from '~/components/ui/processing-demo';

function HeroIllustration(): React.JSX.Element {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.4 }}
            className="relative mt-3 lg:mt-6"
        >
            <div className="relative mb-1 w-full rounded-xl border shadow-2xl bg-background dark:border-none dark:bg-background">
                <SupportiveDashedGridLines />
                <div className="relative z-20">
                    <ProcessingDemo />
                </div>
            </div>
        </motion.div>
    );
}

export function Hero(): React.JSX.Element {
    return (
        <GridSection className="overflow-x-hidden">
            <MainDashedGridLines />
            <div className="mx-auto mt-16 flex flex-col gap-6 px-2 sm:mt-20 sm:px-1 md:mt-24 lg:mt-32">
                <div className="gap-2">
                    <HeroPill />
                    <HeroTitle />
                </div>
                <HeroDescription />
                <HeroButtons />
                <HeroIllustration />
            </div>
        </GridSection>
    );
}

